/* eslint-disable import/no-unresolved */

'use client';

import dynamic from 'next/dynamic';
import React, { useContext } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { buttonVariants } from '@/components/ui/button';
import Context from '@/context';
import replaceUrlHelper from '@/helpers/replaceUrl';

const HomeBannerAnimation = dynamic(() => import('./layouts/HomeBannerAnimation'), {
  ssr: false,
});

export default function HomeBanner({ data, id }) {
  const items = data?.items || [];

  if (!items.length) return null;

  const item = items[0];
  const itemTitle = item?.content?.title || null;
  const itemText = item?.content?.htmlText || null;
  const itemLinks = item?.content?.links || [];
  const image = item?.content?.image;
  const mobileImage = item?.content?.mobileImage;
  const videoUrl = item?.content?.videoUrl;
  const mobileVideoUrl = item?.content?.mobileVideoUrl;

  const { lang } = useContext(Context);

  const replaceUrl = (url) => replaceUrlHelper(url, lang);

  return (
    <section
      id={id}
      className="relative overflow-hidden bg-background-primary before:absolute before:top-0 before:z-10 before:!block before:h-[245px] before:w-full before:bg-custom-gradient before:content-[''] md:min-h-[550px] lg:h-[80vh] before:lg:!hidden xl:min-h-[660px] 2xl:min-h-[720px] 2xll:h-[90vh] 2xll:min-h-[760px]">
      <div className="grid h-full grid-flow-row-dense lg:grid-cols-2">
        <div className="2xll:max-w-non order-last flex flex-col pt-10 lg:order-first lg:max-w-[115%] lg:pt-44 2xl:pt-60 3xl:items-end 3xl:pt-52 ltr:2xll:pr-20 ltr:3xl:pr-28 rtl:pl-8 rtl:2xll:pl-20 rtl:3xl:pl-28">
          <div className="prose order-first pb-12 lg:order-last lg:max-w-167 lg:pb-0 ltr:lg:pl-8 ltr:3xl:pl-4 rtl:lg:pr-8 rtl:3xl:pr-4">
            <div className="mySwiper lg:max-w-167">
              <div className="!w-full">
                <div className="flex flex-col gap-6 px-8 lg:px-0 ltr:md:gap-y-6 ltr:xl:gap-y-12 rtl:md:gap-y-8">
                  {itemTitle && <h1>{itemTitle}</h1>}
                  <div className="flex flex-col gap-y-8 ltr:md:gap-y-8 ltr:lg:pl-10 ltr:xl:gap-y-14 ltr:xl:pl-35 rtl:md:gap-y-12 rtl:lg:pr-10 rtl:xl:pr-35">
                    {/* eslint-disable-next-line react/no-danger */}
                    {itemText && <div dangerouslySetInnerHTML={{ __html: itemText }} />}
                    {itemLinks.length > 0 && (
                      <div className="flex w-full flex-col gap-4 pb-10 md:w-auto md:flex-row md:gap-6">
                        {itemLinks.map((link, linkIndex) => {
                          const linkUrl = link?.url || '#';
                          const linkTarget = link?.target || '_self';
                          const linkLabel = link?.name || null;
                          return (
                            linkLabel && (
                              <Link
                                key={linkIndex}
                                href={replaceUrl(linkUrl)}
                                target={linkTarget}
                                className={`${buttonVariants({
                                  variant: 'primary',
                                })} home-banner-button flex w-full justify-center md:w-fit`}
                                aria-label="home-banner-banner">
                                {linkLabel}
                              </Link>
                            )
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="relative w-full overflow-hidden md:h-full lg:max-w-[85%] 2xll:max-w-none ltr:lg:ml-auto ltr:lg:mr-0 rtl:lg:ml-0 rtl:lg:mr-auto">
          {/* Desktop content - hidden on mobile */}
          <div className="relative hidden h-full w-full lg:block">
            {videoUrl ? (
              <video
                muted
                playsInline
                autoPlay
                loop
                preload="metadata"
                poster={image?.url}
                className="!h-auto min-h-115 w-full object-cover lg:!h-full">
                <source src={videoUrl} type="video/mp4" />
              </video>
            ) : (
              image?.url && (
                <Image
                  src={image.url}
                  alt={itemTitle || 'Banner Image'}
                  fill
                  priority
                  quality={85}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="!h-auto min-h-115 w-full object-cover lg:!h-full"
                />
              )
            )}
          </div>

          {/* Mobile content - hidden on desktop */}
          <div className="relative min-h-115 lg:hidden">
            {mobileVideoUrl ? (
              <video
                width={430}
                height={400}
                muted
                playsInline
                autoPlay
                loop
                preload="metadata"
                className="relative z-10 aspect-video !h-auto min-h-115 w-full object-cover">
                <source src={mobileVideoUrl} type="video/mp4" />
              </video>
            ) : (
              <Image
                src={mobileImage?.url || image?.url}
                alt={itemTitle || 'Banner Image'}
                fill
                priority
                quality={85}
                sizes="100vw"
                className="!relative !h-auto min-h-115 w-full object-cover"
              />
            )}
          </div>
          <div className="absolute bottom-0 flex h-1/2 w-[60%] items-end justify-end opacity-100 mix-blend-multiply lg:hidden lg:h-auto ltr:right-0 rtl:left-0 rtl:scale-x-[-1]">
            <HomeBannerAnimation />
          </div>
        </div>
      </div>
      <div className="home-banner absolute bottom-0 hidden aspect-square h-1/2 items-end justify-end opacity-100 mix-blend-multiply lg:flex ltr:right-0 rtl:left-0 rtl:scale-x-[-1]">
        <HomeBannerAnimation />
      </div>
    </section>
  );
}
